import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Selo, useDialogo } from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';

interface Pendencia {
  readonly tipo: 'CONTRATO' | 'PRE_CADASTRO' | 'CRONOGRAMA_VENCIDO' | 'SOLICITACAO_FECHAMENTO';
  readonly titulo: string;
  readonly descricao: string;
  readonly quantidade: number;
  readonly destino: string;
}

/**
 * RN-125 — As pendências no canto da barra, não numa entrada de menu.
 *
 * ## Por que saiu do menu
 *
 * O menu é para **ir a um lugar**; notificação é para **saber que algo mudou**.
 * Como item de menu, ela só era vista por quem já tinha ido lá — e o número que
 * importa (quantos contratos travados agora) ficava escondido atrás de um
 * clique.
 *
 * O contador aparece na barra de todas as telas. É a mesma informação, no lugar
 * onde ela é olhada de passagem.
 *
 * ## Sem "marcar como lida" — mas com "Limpar" (27/10/2026)
 *
 * Uma pendência sai da lista quando o trabalho é feito, não quando alguém a
 * dispensa. O contador zerado com sete contratos parados seria pior que
 * contador nenhum.
 *
 * A transição de sistemas deixou centenas de pendências antigas, que
 * escondiam as novas. "Limpar notificações" faz o sininho desta pessoa contar
 * só o que acontecer dali em diante — sem dar nada como resolvido: a página
 * "Ver todas" e as listas continuam mostrando tudo, e "Mostrar as antigas"
 * desfaz.
 */
export function SinoDeNotificacoes(): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const limpeza = useQuery({
    queryKey: ['notificacoes', 'limpeza'],
    queryFn: () => requisicaoAutenticada<{ limpasEm: string | null }>('/icms/notificacoes/limpeza'),
  });
  const recarregar = (): void => void queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  const limpar = useMutation({
    mutationFn: () => requisicaoAutenticada('/icms/notificacoes/limpeza', { metodo: 'POST' }),
    onSuccess: recarregar,
  });
  const mostrarAntigas = useMutation({
    mutationFn: () => requisicaoAutenticada('/icms/notificacoes/limpeza', { metodo: 'DELETE' }),
    onSuccess: recarregar,
  });
  const limpasEm = limpeza.data?.limpasEm ?? null;

  const pendencias = useQuery({
    queryKey: ['notificacoes', 'sino'],
    queryFn: () => requisicaoAutenticada<Pendencia[]>('/icms/notificacoes'),
    // A fila muda com o trabalho de outras pessoas; sem isso, quem deixa a tela
    // aberta a manhã inteira vê o número de quando entrou.
    refetchInterval: 120_000,
  });

  // Fecha ao clicar fora e no Esc — um painel que só fecha pelo próprio botão
  // fica no caminho de quem já foi olhar outra coisa.
  useEffect(() => {
    if (!aberto) return;

    const aoClicar = (e: MouseEvent): void => {
      if (caixa.current !== null && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAberto(false);
    };

    document.addEventListener('mousedown', aoClicar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const itens = pendencias.data ?? [];
  const total = itens.reduce((soma, p) => soma + p.quantidade, 0);

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        aria-label={total === 0 ? 'Notificações' : `Notificações: ${total} pendência(s)`}
        className="relative flex h-9 w-9 items-center justify-center rounded-card text-conteudo-secundario transition-colors hover:bg-superficie-elevada hover:text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>

        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-erro px-1 text-[0.625rem] font-semibold leading-none text-superficie-base">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 top-11 z-30 w-80 overflow-hidden rounded-painel border border-borda bg-superficie-elevada shadow-cartao">
          <div className="border-b border-borda px-4 py-3">
            <p className="text-sm font-semibold text-conteudo-primario">Pendências</p>
            <p className="mt-0.5 text-xs text-conteudo-sutil">
              {limpasEm === null
                ? 'Somem quando o trabalho é feito, não quando são dispensadas.'
                : `Só o que mudou desde ${new Date(limpasEm).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}, quando você limpou.`}
            </p>
          </div>

          {itens.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-conteudo-sutil">
              {pendencias.isPending ? 'Carregando…' : 'Nada esperando você.'}
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {itens.map((p) => (
                <li
                  key={`${p.tipo}:${p.titulo}`}
                  className="border-b border-borda/60 last:border-0"
                >
                  <Link
                    to={p.destino}
                    onClick={() => setAberto(false)}
                    className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-superficie-base focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-acento-ciano"
                  >
                    <Selo tom={tomDoTipo(p.tipo)} tamanho="pequeno">
                      {p.quantidade}
                    </Selo>
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm text-conteudo-primario">{p.titulo}</span>
                      <span className="text-xs text-conteudo-sutil">{p.descricao}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/*
            O painel mostra o resumo; a página mostra a mesma coisa com espaço
            para ler. Sem este link ela ficaria acessível só por URL digitada —
            rota que existe e ninguém alcança é rota que apodrece.
          */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-borda px-4 py-2">
            <Link
              to="/notificacoes"
              onClick={() => setAberto(false)}
              className="text-xs text-acento-ciano underline-offset-4 hover:underline"
            >
              Ver todas as pendências
            </Link>
            {limpasEm !== null && (
              <button
                type="button"
                disabled={mostrarAntigas.isPending}
                onClick={() => mostrarAntigas.mutate()}
                className="text-xs text-conteudo-secundario underline-offset-4 hover:underline disabled:opacity-50"
              >
                Mostrar as antigas
              </button>
            )}
            {itens.length > 0 && (
              <button
                type="button"
                disabled={limpar.isPending}
                onClick={async () => {
                  const ok = await dialogo.confirmar({
                    titulo: 'Limpar as notificações?',
                    descricao:
                      'As pendências de agora saem do seu sininho, e ele passa a mostrar só o que acontecer daqui em diante.\n\nNada é dado como resolvido: elas continuam em "Ver todas as pendências" e nas listas de cada tela. Dá para voltar com "Mostrar as antigas".',
                    rotuloConfirmar: 'Limpar',
                  });
                  if (ok) limpar.mutate();
                }}
                className="text-xs text-conteudo-secundario underline-offset-4 hover:underline disabled:opacity-50"
              >
                Limpar notificações
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function tomDoTipo(tipo: Pendencia['tipo']): 'atencao' | 'progresso' | 'critico' {
  // Vencido já custou prazo; os outros estão dentro do fluxo normal.
  if (tipo === 'CRONOGRAMA_VENCIDO') return 'critico';
  if (tipo === 'CONTRATO') return 'progresso';
  return 'atencao';
}
